import React, { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Skeleton } from "../../../components/Skeleton";
import {
  FileText,
  Calendar,
  Users,
  Clock,
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Eye,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import {
  obterRelatorioSemanal,
  obterHistoricoRelatorios,
  obterDetalhesRelatorio,
} from "../../../services/horariosOficiaisService";
import type {
  RelatorioSemanalResponse,
  HistoricoRelatoriosResponse,
  RelatorioOficialDetalhado,
} from "../../../types/horariosOficiais";
import { useSnackbar } from "notistack";

interface TabRelatoriosProps {
  podeVisualizar: boolean;
}

export const TabRelatorios: React.FC<TabRelatoriosProps> = ({ podeVisualizar }) => {
  const [relatorioAtual, setRelatorioAtual] = useState<RelatorioSemanalResponse | null>(null);
  const [historico, setHistorico] = useState<HistoricoRelatoriosResponse | null>(null);
  const [detalhes, setDetalhes] = useState<RelatorioSemanalResponse | null>(null);
  const [loadingAtual, setLoadingAtual] = useState(false);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  const [loadingDetalhes, setLoadingDetalhes] = useState(false);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [semanaDetalheSelecionada, setSemanaDetalheSelecionada] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const LIMITE_POR_PAGINA = 10;

  // Carregar relatório da semana atual
  useEffect(() => {
    if (podeVisualizar) {
      carregarRelatorioAtual();
    }
  }, [podeVisualizar]);

  // Carregar histórico quando mudar de página
  useEffect(() => {
    if (podeVisualizar) {
      carregarHistorico();
    }
  }, [paginaAtual, podeVisualizar]);

  const carregarRelatorioAtual = async () => {
    setLoadingAtual(true);
    try {
      const data = await obterRelatorioSemanal();
      setRelatorioAtual(data);
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao carregar relatório atual", { variant: "error" });
    } finally {
      setLoadingAtual(false);
    }
  };

  const carregarHistorico = async () => {
    setLoadingHistorico(true);
    try {
      const data = await obterHistoricoRelatorios(paginaAtual, LIMITE_POR_PAGINA);
      setHistorico(data);
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao carregar histórico", { variant: "error" });
    } finally {
      setLoadingHistorico(false);
    }
  };

  const carregarDetalhes = async (semanaInicio: string) => {
    setLoadingDetalhes(true);
    setSemanaDetalheSelecionada(semanaInicio);
    try {
      const data = await obterDetalhesRelatorio(semanaInicio);
      setDetalhes(data);
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao carregar detalhes", { variant: "error" });
    } finally {
      setLoadingDetalhes(false);
    }
  };

  const fecharDetalhes = () => {
    setSemanaDetalheSelecionada(null);
    setDetalhes(null);
  };

  const formatarData = (data: string) => {
    const [ano, mes, dia] = data.split("-");
    return `${dia}/${mes}/${ano}`;
  };

  const formatarPeriodo = (inicio: string, fim: string) => {
    return `${formatarData(inicio)} a ${formatarData(fim)}`;
  };

  const calcularPorcentagem = (valor: number, total: number) => {
    if (total === 0) return 0;
    return Math.round((valor / total) * 100);
  };

  const renderResumo = (resumo: RelatorioSemanalResponse["resumo"]) => {
    const total = resumo.total_oficiais;
    const porcentagemMarcados = calcularPorcentagem(resumo.marcaram_todos_dias + resumo.marcaram_parcial, total);
    const porcentagemNaoMarcados = calcularPorcentagem(resumo.nao_marcaram, total);

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="w-4 h-4" />
              Total de Oficiais
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{total}</div>
          </CardContent>
        </Card>

        <Card className="border-green-200 bg-green-50 dark:bg-green-950/20">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2 text-green-700 dark:text-green-400">
              <CheckCircle2 className="w-4 h-4" />
              Completos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700 dark:text-green-400">
              {resumo.marcaram_todos_dias}
            </div>
            <div className="text-xs text-green-600 dark:text-green-500 mt-1">
              Marcaram todos os dias
            </div>
          </CardContent>
        </Card>

        <Card className="border-yellow-200 bg-yellow-50 dark:bg-yellow-950/20">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2 text-yellow-700 dark:text-yellow-400">
              <Clock className="w-4 h-4" />
              Parciais
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-700 dark:text-yellow-400">
              {resumo.marcaram_parcial}
            </div>
            <div className="text-xs text-yellow-600 dark:text-yellow-500 mt-1">
              Marcaram alguns dias
            </div>
          </CardContent>
        </Card>

        <Card className="border-red-200 bg-red-50 dark:bg-red-950/20">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2 text-red-700 dark:text-red-400">
              <AlertCircle className="w-4 h-4" />
              Não Marcaram
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-700 dark:text-red-400">
              {resumo.nao_marcaram}
            </div>
            <div className="text-xs text-red-600 dark:text-red-500 mt-1">
              {porcentagemNaoMarcados}% do total
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderTabelaOficiais = (oficiais: RelatorioOficialDetalhado[]) => {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Detalhamento por Oficial
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 font-medium">Oficial</th>
                  <th className="text-left p-3 font-medium">Patente</th>
                  <th className="text-center p-3 font-medium">Dias Marcados</th>
                  <th className="text-center p-3 font-medium">Dias Ausente</th>
                  <th className="text-center p-3 font-medium">Não Marcou</th>
                  <th className="text-center p-3 font-medium">Total Horas</th>
                  <th className="text-center p-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {oficiais.map((oficial, index) => {
                  const totalDiasMarcados = oficial.dias_marcados.length;
                  const totalDiasAusente = oficial.dias_ausente.length;
                  const totalDiasNaoMarcou = oficial.dias_nao_marcados.length;

                  // Lógica corrigida: Completo = não tem dias pendentes E tem alguma marcação
                  const marcouTudo = totalDiasNaoMarcou === 0 && (totalDiasMarcados > 0 || totalDiasAusente > 0);
                  const marcouParcial = (totalDiasMarcados > 0 || totalDiasAusente > 0) && totalDiasNaoMarcou > 0;
                  const naoMarcou = totalDiasMarcados === 0 && totalDiasAusente === 0;

                  return (
                    <tr key={index} className="border-b hover:bg-gray-50 dark:hover:bg-gray-900">
                      <td className="p-3">{oficial.nick}</td>
                      <td className="p-3">
                        <Badge variant="outline">{oficial.patente}</Badge>
                      </td>
                      <td className="p-3 text-center">
                        <Badge variant={totalDiasMarcados > 0 ? "default" : "secondary"}>
                          {totalDiasMarcados}
                        </Badge>
                      </td>
                      <td className="p-3 text-center">
                        <Badge variant={totalDiasAusente > 0 ? "secondary" : "outline"}>
                          {totalDiasAusente}
                        </Badge>
                      </td>
                      <td className="p-3 text-center">
                        <Badge variant={totalDiasNaoMarcou > 0 ? "destructive" : "outline"}>
                          {totalDiasNaoMarcou}
                        </Badge>
                      </td>
                      <td className="p-3 text-center font-medium">
                        {oficial.total_horas.toFixed(1).replace('.', ',')}h
                      </td>
                      <td className="p-3 text-center">
                        {marcouTudo && (
                          <Badge className="bg-green-500 hover:bg-green-600">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Completo
                          </Badge>
                        )}
                        {marcouParcial && (
                          <Badge variant="secondary">
                            <Clock className="w-3 h-3 mr-1" />
                            Parcial
                          </Badge>
                        )}
                        {naoMarcou && (
                          <Badge variant="destructive">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            Pendente
                          </Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (!podeVisualizar) {
    return (
      <Card className="border-red-200 bg-red-50 dark:bg-red-950/20">
        <CardHeader>
          <CardTitle className="text-red-700 dark:text-red-400">Acesso Negado</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-red-600 dark:text-red-500">
            Você não tem permissão para visualizar relatórios de horários.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Visualização de detalhes de uma semana específica
  if (semanaDetalheSelecionada && detalhes) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Relatório da Semana
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {formatarPeriodo(detalhes.semana.inicio, detalhes.semana.fim)}
            </p>
          </div>
          <Button variant="outline" onClick={fecharDetalhes}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Voltar ao Histórico
          </Button>
        </div>

        {loadingDetalhes ? (
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <>
            {renderResumo(detalhes.resumo)}
            {renderTabelaOficiais(detalhes.oficiais)}
          </>
        )}
      </div>
    );
  }

  return (
    <Tabs defaultValue="atual" className="w-full">
      <TabsList className="grid w-full max-w-md grid-cols-2">
        <TabsTrigger value="atual">Semana Atual</TabsTrigger>
        <TabsTrigger value="historico">Histórico</TabsTrigger>
      </TabsList>

      <TabsContent value="atual" className="space-y-4 mt-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Relatório em Andamento
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {relatorioAtual && formatarPeriodo(relatorioAtual.semana.inicio, relatorioAtual.semana.fim)}
            </p>
          </div>
          <Button variant="outline" onClick={carregarRelatorioAtual} disabled={loadingAtual}>
            Atualizar
          </Button>
        </div>

        {loadingAtual ? (
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : relatorioAtual ? (
          <>
            {renderResumo(relatorioAtual.resumo)}
            {renderTabelaOficiais(relatorioAtual.oficiais)}
          </>
        ) : (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nenhum relatório disponível para esta semana.</p>
            </CardContent>
          </Card>
        )}
      </TabsContent>

      <TabsContent value="historico" className="space-y-4 mt-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Relatórios Anteriores
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Consulte relatórios de semanas passadas
            </p>
          </div>
        </div>

        {loadingHistorico ? (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        ) : historico && historico.relatorios.length > 0 ? (
          <>
            <div className="space-y-2">
              {historico.relatorios.map((item, index) => {

                return (
                  <Card key={index} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Calendar className="w-5 h-5 text-muted-foreground" />
                            <span className="font-medium">
                              {formatarPeriodo(item.semana_inicio, item.semana_fim)}
                            </span>
                          </div>
                          <div className="flex gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4 text-blue-600" />
                              Gerado em {formatarData(item.gerado_em.split('T')[0])}
                            </span>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => carregarDetalhes(item.semana_inicio)}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Ver Detalhes
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Paginação */}
            {historico.total_paginas > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPaginaAtual((prev) => Math.max(1, prev - 1))}
                  disabled={paginaAtual === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Anterior
                </Button>
                <span className="text-sm text-muted-foreground px-4">
                  Página {paginaAtual} de {historico.total_paginas}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPaginaAtual((prev) => Math.min(historico.total_paginas, prev + 1))}
                  disabled={paginaAtual === historico.total_paginas}
                >
                  Próxima
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </>
        ) : (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nenhum relatório encontrado no histórico.</p>
              <p className="text-xs mt-2">
                Os relatórios são gerados automaticamente todas as segundas-feiras às 04:00.
              </p>
            </CardContent>
          </Card>
        )}
      </TabsContent>
    </Tabs>
  );
};
